import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { Strategy } from '@/types/strategy';
import StrategyCard from '@/pages/strategies/components/StrategyCard';
import { destroyTrailingEngine } from '@/lib/trailing-stop-engine';
import { destroyChinEngine } from '@/lib/chin-bot-engine';
import { destroyAutoChinEngine } from '@/lib/auto-chin-bot-engine';


interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface StrategyGridProps {
  selectedAccount: CtraderAccount | null;
  onStrategiesLoaded?: (strategies: Strategy[]) => void;
}

export default function StrategyGrid({ selectedAccount, onStrategiesLoaded }: StrategyGridProps) {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStrategies([]);
        onStrategiesLoaded?.([]);
        setLoading(false);
        return;
      }

      const { data, error: dbError } = await supabase
        .from('strategies')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (dbError) throw dbError;

      let result: Strategy[];
      if (data && data.length > 0) {
        result = data.map((row: Record<string, unknown>): Strategy => {
          const rawType = (row.type as string) || 'generic';
          const normalizedType = rawType === 'trailing_stop' ? 'trailingStop' : rawType;
          return {
            id: row.id as string,
            user_id: row.user_id as string,
            name: row.name as string,
            type: normalizedType as Strategy['type'],
            symbol: row.symbol as string,
            symbol_list_id: (row.symbol_list_id as string | null) || null,
            timeframe: row.timeframe as string,
            description: (row.description as string) || '',
            indicators: (row.indicators as Strategy['indicators']) || [],
            entry_rules: (row.entry_rules as Strategy['entry_rules']) || [],
            exit_rules: (row.exit_rules as Strategy['exit_rules']) || [],
            risk_per_trade: row.risk_per_trade as number,
            max_positions: row.max_positions as number,
            stop_loss_pips: row.stop_loss_pips as number,
            take_profit_pips: row.take_profit_pips as number,
            mt4_copy_enabled: (row.mt4_copy_enabled as boolean) || false,
            ctrader_copy_enabled: (row.ctrader_copy_enabled as boolean) || false,
            assigned_accounts: (row.assigned_accounts as Strategy['assigned_accounts']) || [],
            is_active: (row.is_active as boolean) || false,
            active_accounts: (row.active_accounts as string[]) || [],
            trailingStopParams: (row.trailingstopparams as Strategy['trailingStopParams']) || null,
            chinBotParams: (row.chinparams as Strategy['chinBotParams']) || null,
            created_at: row.created_at as string,
            updated_at: row.updated_at as string,
          };
        });
      } else {
        result = [];
      }
      setStrategies(result);
      onStrategiesLoaded?.(result);
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
      setStrategies([]);
      onStrategiesLoaded?.([]);
      setError('Could not load strategies from server.');
    } finally {
      setLoading(false);
    }
  }, [onStrategiesLoaded]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  const handleToggleActive = async (strategyId: string, accountId: string) => {
    if (!accountId) return;

    const currentStrategies = [...strategies];
    const targetIndex = currentStrategies.findIndex((s) => s.id === strategyId);
    if (targetIndex === -1) return;

    const target = currentStrategies[targetIndex];
    const activeAccounts: string[] = target.active_accounts || [];
    const isCurrentlyActive = activeAccounts.includes(accountId);

    // Optimistic update for THIS account only
    const newActiveAccounts = isCurrentlyActive
      ? activeAccounts.filter((a) => a !== accountId)
      : [...activeAccounts, accountId];

    currentStrategies[targetIndex] = {
      ...target,
      active_accounts: newActiveAccounts,
    };
    setStrategies(currentStrategies);
    onStrategiesLoaded?.(currentStrategies);

    try {
      await supabase
        .from('strategies')
        .update({
          active_accounts: newActiveAccounts,
          updated_at: new Date().toISOString(),
        })
        .eq('id', strategyId);

      // If turning OFF, destroy runner/engine for this strategy+account
      if (!isCurrentlyActive) {
        // Was off, now turning ON — nothing to destroy
      } else {
        // Was on, now turning OFF for THIS account
        if (target.type === 'chin') {
          destroyChinEngine(strategyId, accountId);
        }
        if (target.type === 'autochinbot') {
          destroyAutoChinEngine(strategyId, accountId);
        }
        // Trailing engine is per-account — destroy only the engine of THIS account
        if (target.type === 'trailingStop') {
          destroyTrailingEngine(strategyId, accountId);
        }
      }
    } catch {
      // Revert on error
      setStrategies(strategies);
      onStrategiesLoaded?.(strategies);
    }
  };

  // All strategies are shared across all accounts — but active state is per-account
  const filteredStrategies = strategies;

  // Compute active/inactive counts for the SELECTED account only
  const activeCount = selectedAccount
    ? filteredStrategies.filter((s) => (s.active_accounts || []).includes(selectedAccount.account_id)).length
    : 0;
  const inactiveCount = selectedAccount
    ? filteredStrategies.length - activeCount
    : 0;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-3">
          <div className="h-5 w-32 bg-background-200/70 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
              <div className="h-4 w-3/4 bg-background-200/70 rounded animate-pulse mb-3"></div>
              <div className="h-3 w-1/2 bg-background-200/50 rounded animate-pulse mb-4"></div>
              <div className="h-8 w-full bg-background-200/50 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
            <i className="ri-flashlight-line text-primary-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">
              Strategies
            </h3>
            <p className="text-[11px] text-foreground-500">
              {filteredStrategies.length} strategy{filteredStrategies.length !== 1 ? 'ies' : ''}
              {selectedAccount && activeCount > 0 && <span className="text-emerald-400"> · {activeCount} active</span>}
              {selectedAccount && inactiveCount > 0 && <span className="text-foreground-400"> · {inactiveCount} paused</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchStrategies}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer"
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-amber-400 text-xs mt-0.5"></i>
          <p className="text-amber-600 text-xs">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {filteredStrategies.length === 0 && (
        <div className="bg-background-100 rounded-xl border border-dashed border-background-300/60 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-4">
            <i className="ri-flashlight-line text-foreground-400 text-2xl"></i>
          </div>
          <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">No strategies assigned</h4>
          <p className="text-xs text-foreground-500 mb-5 max-w-sm mx-auto">
            {selectedAccount
              ? 'No strategies available for this account yet. Chat with the admin to create a new bot.'
              : 'No strategies available. Chat with the admin to create a new bot.'}
          </p>
        </div>
      )}

      {/* Strategy grid */}
      {filteredStrategies.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredStrategies.map((strategy) => {
            const isActiveForThisAccount = selectedAccount
              ? (strategy.active_accounts || []).includes(selectedAccount.account_id)
              : false;

            return (
              <StrategyCard
                key={strategy.id}
                strategy={strategy}
                isActive={isActiveForThisAccount}
                onToggleActive={(strategyId) => {
                  if (selectedAccount) {
                    handleToggleActive(strategyId, selectedAccount.account_id);
                  }
                }}
                selectedAccountId={selectedAccount?.account_id || null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}